import type { LearningDuration } from '../../types/learning';

type Clock = () => number;

export type StudyActivityTimerStateV2 = {
  startedAtMs: number;
  activeAccumulatedMs: number;
  pausedAccumulatedMs: number;
  savedAtMs: number;
  wasPaused: boolean;
};

/**
 * LE-V2-only activity timer with browser refresh persistence.
 * Browser-away time is treated as paused, never as active learning time.
 * Mathematics v1 continues using the unchanged shared ActivityTimer.
 */
export class StudyActivityTimerV2 {
  private readonly startedAt: number;
  private activeStartedAt: number | null;
  private pausedStartedAt: number | null = null;
  private activeAccumulatedMs = 0;
  private pausedAccumulatedMs = 0;

  constructor(private readonly now: Clock = () => Date.now(), initialState?: StudyActivityTimerStateV2) {
    const current = this.now();
    if (initialState) {
      this.startedAt = initialState.startedAtMs;
      this.activeAccumulatedMs = initialState.activeAccumulatedMs;
      this.pausedAccumulatedMs = initialState.pausedAccumulatedMs + Math.max(0, current - initialState.savedAtMs);
      this.activeStartedAt = initialState.wasPaused ? null : current;
      this.pausedStartedAt = initialState.wasPaused ? current : null;
      return;
    }
    this.startedAt = current;
    this.activeStartedAt = current;
  }

  pause() {
    if (this.pausedStartedAt !== null) return;
    const current = this.now();
    if (this.activeStartedAt !== null) {
      this.activeAccumulatedMs += Math.max(0, current - this.activeStartedAt);
      this.activeStartedAt = null;
    }
    this.pausedStartedAt = current;
  }

  resume() {
    if (this.pausedStartedAt === null) return;
    const current = this.now();
    this.pausedAccumulatedMs += Math.max(0, current - this.pausedStartedAt);
    this.pausedStartedAt = null;
    this.activeStartedAt = current;
  }

  snapshot(): LearningDuration {
    const current = this.now();
    const activeDurationMs = this.activeAccumulatedMs +
      (this.activeStartedAt === null ? 0 : Math.max(0, current - this.activeStartedAt));
    const pausedDurationMs = this.pausedAccumulatedMs +
      (this.pausedStartedAt === null ? 0 : Math.max(0, current - this.pausedStartedAt));
    return {
      elapsedDurationMs: Math.max(0, current - this.startedAt),
      activeDurationMs,
      pausedDurationMs,
    };
  }

  exportState(): StudyActivityTimerStateV2 {
    const current = this.now();
    const snapshot = this.snapshot();
    return {
      startedAtMs: this.startedAt,
      activeAccumulatedMs: snapshot.activeDurationMs,
      pausedAccumulatedMs: snapshot.pausedDurationMs,
      savedAtMs: current,
      wasPaused: this.pausedStartedAt !== null,
    };
  }
}
